import { deepDestringifyFunctions, destringifyGlobalScope } from '@symbo.ls/utils'

// FA204 / frank trap (project_frank_drops_module_local_helpers) — the SDK
// singleton accessor (`getSDK`/`awaitSDKInit`/`awaitCrossAppHydration`)
// moved to ./sdkClient.js: it used to be bare-called from a DOZEN exports
// below, and duplicating that whole stateful singleton a dozen times would
// have meant a dozen INDEPENDENT copies silently diverging from real
// singleton semantics. A cross-file import resolves fine via frank's
// globalScope fallback, so importing it here — like every OTHER export in
// this file now does — needs no duplication at all. Re-exported below so
// this file's own public surface (and the functions/index.js barrel) is
// unchanged for every existing consumer.
//
// Every OTHER module-scope helper/table in THIS file (readCache/writeCache/
// USER_PROFILE_*/USER_ORGS_*/_splitProjectRef/_ENV_CFG_TTL/_envCfgFetch)
// still gets the usual per-export self-contained inline copy; the in-flight-
// dedup promises (`USER_PROFILE_PROMISE`/`USER_ORGS_PROMISE`) are genuine
// cross-call state with no el/root argument to hoist them onto — re-homed
// onto `globalThis.__sdkUserDataCache`, the aiService.js/pointerCapability.js
// template.
import { getSDK, awaitSDKInit, awaitCrossAppHydration } from './sdkClient.js'

export { getSDK, awaitSDKInit, awaitCrossAppHydration }

// ── Auth ────────────────────────────────────────────────────────────────────

export async function getMe(opts) {
  const result = await getSDK(opts).getMe()
  return result?.data || null
}

export async function login(email, password, opts) {
  return getSDK(opts).login(email, password)
}

// Renamed from `logout` (2026-08-09, frank-compile pass) — collided with
// `switchers/logout.js`'s `logout(state, sdk, queryClient)` (the full
// sign-out flow: state wipe + cache clear + redirect) once both landed in
// the SAME `functions/index.js` barrel via `export *`. Two `export *`
// sources providing the same name is an ES module "ambiguous export" —
// the name is silently dropped from the merged namespace entirely (frank's
// `frank to-json` dropped-export check caught this: `functions.logout` was
// missing from the emitted bundle). This thinner SDK-only wrapper had zero
// live consumers (checked: no `el.call('logout', …)`, no direct import) —
// renamed rather than deleted so the SDK-parity wrapper (`login`/`sdkLogout`)
// stays available for a future caller that wants JUST the SDK call without
// the full local-state teardown.
export async function sdkLogout(opts) {
  return getSDK(opts).logout()
}

export function isAuthenticated(opts) {
  return getSDK(opts).isReady()
}

// localStorage-backed caches so every tab/reload doesn't re-hit
// `/me` and `/organizations` — the API rate-limits hard when multiple
// tabs open at once, and a cold reload after the tab was closed
// otherwise rendered an empty dashboard for the duration of the
// network round-trip. Each cache also dedupes concurrent callers
// within a single tab via a shared in-flight promise. Cleared on
// `invalidateUserDataCache()` (logout, auth error).
// Synchronous reads — return whatever is in localStorage right now,
// regardless of TTL. Callers use this to seed state instantly on mount,
// then await the async cached fetcher to revalidate in the background.
// Returns `null` if nothing is cached.
export function readCachedUserProfile() {
  const USER_PROFILE_KEY = '__canvas_user_profile_v1'
  function readCache(key) {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      const entry = JSON.parse(raw)
      if (!entry?.at) return null
      return entry
    } catch {
      return null
    }
  }
  return readCache(USER_PROFILE_KEY)?.data || null
}

export function readCachedOrgs() {
  const USER_ORGS_KEY = '__canvas_user_orgs_v1'
  function readCache(key) {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      const entry = JSON.parse(raw)
      if (!entry?.at) return null
      return entry
    } catch {
      return null
    }
  }
  return readCache(USER_ORGS_KEY)?.data || null
}

export async function getUserProfileCached(opts) {
  function sdkUserDataCache() {
    return (globalThis.__sdkUserDataCache = globalThis.__sdkUserDataCache || {
      userProfilePromise: null,
      userOrgsPromise: null
    })
  }
  const USER_PROFILE_TTL_MS = 5 * 60_000
  const USER_PROFILE_KEY = '__canvas_user_profile_v1'
  function readCache(key) {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      const entry = JSON.parse(raw)
      if (!entry?.at) return null
      return entry
    } catch {
      return null
    }
  }
  function writeCache(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify({ at: Date.now(), data }))
    } catch {}
  }

  const cache = sdkUserDataCache()
  const entry = readCache(USER_PROFILE_KEY)
  if (entry && Date.now() - entry.at < USER_PROFILE_TTL_MS) return entry.data
  if (cache.userProfilePromise) return cache.userProfilePromise
  const sdk = getSDK(opts)
  cache.userProfilePromise = Promise.resolve(sdk.getUserProfile())
    .then((r) => {
      const data = r?.data || r || null
      if (data) writeCache(USER_PROFILE_KEY, data)
      return data
    })
    .catch((err) => {
      // Fall back to stale cache on 429/network errors — stale user data
      // beats a broken dashboard. Only re-throw when we have nothing.
      if (entry?.data) return entry.data
      throw err
    })
    .finally(() => {
      cache.userProfilePromise = null
    })
  return cache.userProfilePromise
}

export async function listOrganizationsCached(opts) {
  function sdkUserDataCache() {
    return (globalThis.__sdkUserDataCache = globalThis.__sdkUserDataCache || {
      userProfilePromise: null,
      userOrgsPromise: null
    })
  }
  const USER_ORGS_TTL_MS = 5 * 60_000
  const USER_ORGS_KEY = '__canvas_user_orgs_v1'
  function readCache(key) {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      const entry = JSON.parse(raw)
      if (!entry?.at) return null
      return entry
    } catch {
      return null
    }
  }
  function writeCache(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify({ at: Date.now(), data }))
    } catch {}
  }

  const cache = sdkUserDataCache()
  const entry = readCache(USER_ORGS_KEY)
  if (entry && Date.now() - entry.at < USER_ORGS_TTL_MS) return entry.data
  if (cache.userOrgsPromise) return cache.userOrgsPromise
  const sdk = getSDK(opts)
  cache.userOrgsPromise = Promise.resolve(sdk.listOrganizations())
    .then((r) => {
      const data = Array.isArray(r) ? r : r?.data || r?.organizations || []
      writeCache(USER_ORGS_KEY, data)
      return data
    })
    .catch((err) => {
      if (entry?.data) return entry.data
      throw err
    })
    .finally(() => {
      cache.userOrgsPromise = null
    })
  return cache.userOrgsPromise
}

// Clear everything keyed under the `__canvas_*` prefix in localStorage.
// Called on logout + auth errors so a re-login with a different account
// can't inherit the previous user's cached profile/org/project list.
export function invalidateUserDataCache() {
  function sdkUserDataCache() {
    return (globalThis.__sdkUserDataCache = globalThis.__sdkUserDataCache || {
      userProfilePromise: null,
      userOrgsPromise: null
    })
  }
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k && k.startsWith('__canvas_')) localStorage.removeItem(k)
    }
  } catch {}
  const cache = sdkUserDataCache()
  cache.userProfilePromise = null
  cache.userOrgsPromise = null
}

// ── Project ─────────────────────────────────────────────────────────────────

// A project payload's `globalScope` bag needs the platform's closure-eval
// revival (destringifyGlobalScope, smbls/packages/utils/object.js — every
// sibling constant/helper declared as a `var` in ONE shared closure, exactly
// what createDomql.js gives a standalone project boot), never the generic
// per-string eval deepDestringifyFunctions gives every other payload field.
// Capture the bag RAW (still frank-serialized strings) before the generic
// walk below consumes it — once deepDestringifyFunctions has run, each
// helper is already a standalone function and the resolver has nothing left
// to build a closure around, so a helper reading a sibling constant/helper
// throws ReferenceError only when called, not when checked
// (FW-GLOBALSCOPE-HELPER-CLOSURE-LOST-IN-PAYLOAD-REVIVE-1).
//
// `deferRevive` (workspace boot fix step 4): top-level parts named here come
// back RAW — still frank strings — for a caller that revives each entry on its
// first read (the workspace app loader: components are 60 % of an app's code
// revive, measured 2026-09-29). Every other part is revived as before, and a
// caller that does not ask gets the whole payload revived, unchanged.
const _reviveProjectPayload = (data, deferRevive) => {
  const rawGlobalScope = data && typeof data === 'object' ? data.globalScope : null
  const defer =
    Array.isArray(deferRevive) && data && typeof data === 'object'
      ? deferRevive.filter((part) => Object.prototype.hasOwnProperty.call(data, part))
      : []
  let revived
  if (defer.length) {
    const eager = {}
    for (const part of Object.keys(data)) if (!defer.includes(part)) eager[part] = data[part]
    revived = deepDestringifyFunctions(eager)
    for (const part of defer) revived[part] = data[part]
  } else {
    revived = deepDestringifyFunctions(data)
  }
  if (rawGlobalScope && typeof rawGlobalScope === 'object' && revived) {
    revived.globalScope = destringifyGlobalScope(rawGlobalScope)
  }
  return revived
}

// Anonymous fetch for public projects — preview subdomains use this so
// project data loads without an auth cookie. Returns null when the project
// isn't public or the env isn't configured; caller can fall back.
// `raw: true` (USER-CODE-ORIGIN security review B3): return the payload
// UNREVIVED — every project function still a string. A realm that holds a
// session (the shell's in-app preview) must never revive project code:
// reviving is `eval('(' + str + ')')`, and an expression's side effects run
// at revive time. The opaque user-code frame revives it instead.
export async function fetchPublicProjectFromSDK(owner, key, { envKey, raw = false, ...sdkOpts } = {}) {
  // Owner + key or nothing: a bare key can name several owners' projects
  // (the public by-key routes answer it 409 ambiguous_key). Every caller has
  // the owner; one without it reads nothing rather than another owner's site.
  if (!envKey || !owner || !key) return null
  const sdk = getSDK(sdkOpts)
  const result = await sdk.getPublicProjectDataByKey({ owner, key }, { envKey })
  const data = result?.data || result
  if (!data) return null
  return typeof data === 'string' || raw ? data : _reviveProjectPayload(data)
}

// Anonymous probe — returns 'public' | 'private' | 'password-protected' | null.
// Used by the preview surface AFTER the env-fallback chain has exhausted, to
// decide between "genuine not-published" vs "redirect to login" vs "show
// password card".
export async function fetchPublicProjectVisibilityFromSDK(owner, key, sdkOpts = {}) {
  if (!owner || !key) return null
  const sdk = getSDK(sdkOpts)
  return sdk.getPublicProjectVisibility({ owner, key })
}

// Anonymous unlock for password-protected projects. `password` is the
// SHA-256 hex digest of the user-typed value (matches the hash stored on
// project.projectPassword). Returns project data on match, null on
// invalid password — caller re-prompts.
export async function unlockPublicProjectFromSDK(
  owner,
  key,
  { envKey, password, raw = false, ...sdkOpts } = {}
) {
  if (!envKey || !password || !owner || !key) return null
  const sdk = getSDK(sdkOpts)
  const result = await sdk.unlockPublicProjectDataByKey({ owner, key }, { envKey, password })
  const data = result?.data || result
  if (!data) return null
  return typeof data === 'string' || raw ? data : _reviveProjectPayload(data)
}

export async function fetchProjectFromSDK(owner, key, options = {}) {
  const sdk = getSDK(options)
  const result = await sdk.getProjectDataByKey(
    { owner, key },
    {
      branch: options.branch || 'main',
      version: options.version || 'latest',
      // Workspace-module read grant (GA onboarding): membership of the
      // workspace that INSTALLED this project authorizes the read, so
      // invited members without a project-level role can still load the
      // module. Older sdk/server pairs ignore the extra key harmlessly.
      ...(options.workspaceId ? { workspaceId: options.workspaceId } : {})
    }
  )
  const data = result?.data || result
  if (!data) return null
  // WS-MODULE-PAYLOAD-CACHE-1 (BF-SIGNIN-LAYOUT-LATENCY-1): write-through
  // persistent cache for the workspace-module payload. This is the ONLY
  // seam where the PRE-revive payload exists (function fields still
  // frank-serialized strings), so caching is a plain JSON round-trip —
  // never a re-frank of live functions. The caller names the slot
  // (payloadCacheKey); identity (owner/key/branch) and the payload's own
  // version ride inside the row. A write failure must never break the
  // fetch — and a half-written slot must never survive to serve.
  if (options.payloadCacheKey && data && typeof data === 'object') {
    try {
      globalThis.localStorage?.setItem(
        options.payloadCacheKey,
        JSON.stringify({
          v: 1,
          at: Date.now(),
          wsId: options.workspaceId || null,
          owner,
          key,
          branch: options.branch || 'main',
          version: data.version || data.latestVersion || null,
          data
        })
      )
    } catch (_) {
      try {
        globalThis.localStorage?.removeItem(options.payloadCacheKey)
      } catch (_2) {}
    }
  }
  // `options.raw` — see fetchPublicProjectFromSDK (security review B3).
  // `options.revive === false` — a background cache refresh needs only the
  // serialized payload and version. Skip compilation there; the next
  // foreground load revives what it uses.
  return typeof data === 'string' || options.raw === true || options.revive === false
    ? data
    : _reviveProjectPayload(data, options.deferRevive)
}

// Split an `owner/key` string into the `{owner, key}` shape the SDK's
// `projectKeyPath` helper expects for the collision-safe 2-seg route.
// Passing the bare string runs `encodeURIComponent` over the whole thing
// — turning `nikoloza/presentation` into `nikoloza%2Fpresentation`,
// which the API treats as a single legacy key (404 against the modern
// owner/key projects). Splitting on the first `/` keeps the slash as a
// path separator. Falls back to a string when no slash is present so
// the helper can still hit the legacy 1-seg route for callers that
// genuinely have a bare key.

// Look up an environment's publish config ({mode, branch, version}) for a
// project by key and distill it into the {branch, version} pair that
// getProjectDataByKey accepts. Returns null when the project has no
// matching env configured — callers then fall back to their own default.
//
// Stale-while-revalidate cache (localStorage, 10-min TTL): the resolution is
// TWO serial round-trips (getProjectByKey → listEnvironments) that measured
// ~2s of every workspace boot's loader window, for a value that changes only
// when someone re-configures a publish env. A fresh cache hit answers
// instantly and still refreshes in the background so the next boot converges;
// the in-session module memo in workspaceProjects.js was already stickier
// than this TTL, so cross-reload staleness here is strictly bounded tighter
// than what the session already tolerated. Failures never cache.

// Cache-key identity for a project ref — routed through the SAME
// `_splitProjectRef` normalization the fetch path uses, so cache identity
// always matches fetch identity. Callers pass BOTH shapes: fetchProject.js
// the 'owner/key' string, the workspace-module loader the `{owner, key}`
// ref object (workspaceProjects.js `_pipeline.resolveEnv`). The object
// shape used to template-stringify into '[object Object]', collapsing
// every project into ONE shared cache slot. Pure — exported for the
// colocated node --test suite.
export const envCfgCacheKey = (projectRef, envKey) => {
  function splitProjectRef(ref) {
    if (!ref) return ref
    if (typeof ref === 'object') return ref
    const idx = String(ref).indexOf('/')
    if (idx === -1) return ref
    return { owner: ref.slice(0, idx), key: ref.slice(idx + 1) }
  }
  const ref = splitProjectRef(projectRef)
  const id = ref && typeof ref === 'object' ? `${ref.owner}/${ref.key}` : String(ref || '')
  return `symbols.envcfg.v1.${id}.${envKey}`
}

export async function resolveEnvPublishConfig(projectKey, envKey, sdkOpts = {}) {
  // FA204 / frank trap — self-contained inline copies of _splitProjectRef/
  // envCfgCacheKey/_envCfgFetch/_ENV_CFG_TTL, see the export above for the
  // canonical envCfgCacheKey shape kept alive for its own test suite.
  function splitProjectRef(ref) {
    if (!ref) return ref
    if (typeof ref === 'object') return ref
    const idx = String(ref).indexOf('/')
    if (idx === -1) return ref
    return { owner: ref.slice(0, idx), key: ref.slice(idx + 1) }
  }
  function cacheKeyFor(projectRef, envKey) {
    const ref = splitProjectRef(projectRef)
    const id = ref && typeof ref === 'object' ? `${ref.owner}/${ref.key}` : String(ref || '')
    return `symbols.envcfg.v1.${id}.${envKey}`
  }
  async function envCfgFetch(projectKey, envKey, sdkOpts) {
    // Owner + key or nothing: a bare key can name several owners' projects
    // (the server answers it 409 `ambiguous_key`), and an env config read for
    // the wrong owner pins the wrong project's version. Every caller sends the
    // owner (fetchProject.js 'owner/key', the module loader { owner, key });
    // a ref without one resolves no config, and the caller keeps its default.
    const ref = splitProjectRef(projectKey)
    if (!ref || typeof ref !== 'object' || !ref.owner || !ref.key) return null
    const sdk = getSDK(sdkOpts)
    const workspaceId = sdkOpts?.workspaceId
    const project = await sdk.getProjectByKey({ owner: ref.owner, key: ref.key }, { workspaceId })
    const projectId = project?._id || project?.id
    if (!projectId) return null
    const envs = await sdk.listEnvironments(projectId, { workspaceId })
    const cfg = envs?.environments?.[envKey]
    if (!cfg || cfg.enabled === false) return null
    const branch = cfg.branch || 'main'
    // 'published' must pin to Project.publishedVersion, the same contract
    // the server's own /public/:env route resolves
    // (ProjectController._resolveEnvServeTarget) — every mode but 'version'
    // used to fall through to 'latest' here, so a plain push reached every
    // tenant whose env was left on 'published'. Falls back to 'latest' only
    // when the project has never published a version at all.
    const version =
      cfg.mode === 'version' && cfg.version
        ? cfg.version
        : cfg.mode === 'published' && project?.publishedVersion?.value
          ? project.publishedVersion.value
          : 'latest'
    return { branch, version }
  }
  const ENV_CFG_TTL = 10 * 60 * 1000

  if (!projectKey || !envKey) return null
  // One-way migration: pre-fix builds stringified object refs into the key
  // ('symbols.envcfg.v1.[object Object].<env>' — one slot shared by every
  // project). Never read it — delete on sight so it can't serve another
  // project's config.
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(`symbols.envcfg.v1.[object Object].${envKey}`)
    }
  } catch (_) {
    /* best-effort */
  }
  let cached
  try {
    const raw =
      typeof localStorage !== 'undefined' && localStorage.getItem(cacheKeyFor(projectKey, envKey))
    cached = raw ? JSON.parse(raw) : null
  } catch (_) {
    cached = null
  }
  const live = envCfgFetch(projectKey, envKey, sdkOpts)
    .then((cfg) => {
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(
            cacheKeyFor(projectKey, envKey),
            JSON.stringify({ cfg, at: Date.now() })
          )
        }
      } catch (_) {
        /* cache is best-effort */
      }
      return cfg
    })
    .catch((e) => {
      // A fresh-enough cache absorbs a transient failure; without one the
      // caller sees the rejection exactly as before.
      if (cached && Date.now() - (cached.at || 0) < ENV_CFG_TTL) return cached.cfg ?? null
      throw e
    })
  if (cached && Date.now() - (cached.at || 0) < ENV_CFG_TTL) {
    // Fresh hit — answer now, let the refresh above land for the next boot.
    void live.catch(() => {})
    return cached.cfg ?? null
  }
  return live
}
